import { AdminDropdownsBaseController } from "./admin_dropdowns_base_controller";
import Rails from "@rails/ujs";

let debounce = require("lodash/debounce");

export default class extends AdminDropdownsBaseController {
  static targets = [
    "menu",
    "component",
    "icon",
    "label",
    "promptOption",
    "hiddenField",
    "multiselectHiddenFieldsContainer",
    "menuFilter",
    "clearFilterIcon",
    "itemsContainer",
    "loadMoreContainer",
    "noResultsMessage",
    "presenceValidationError",
    "infiniteScrollTrigger",
    "itemTemplate",
    "loadingError",
  ];

  static values = {
    endpoint: String,
    direction: String,
    prompt: String,
    placement: String,
    multiselect: Boolean,
    selectedCopy: String,
    perPage: Number,
    searchDelay: Number,
    selected: Array,
    selectedDisplay: Array,
  };

  connect() {
    this.currentPage = 1;
    this.totalPages = 1;
    this.searchTerm = "";
    this.items = [];
    this.selectedItems = new Map();
    this.loadedPages = new Set();

    this.clickOutsideHandler = this._handleOutsideClick.bind(this);
    this.searchHandler = debounce(
      this.performSearch.bind(this),
      this.searchDelayValue || 300,
    );
    this.validatePresenceHandler = this.validatePresence.bind(this);

    if (this.hasPresenceValidationErrorTarget) {
      document.addEventListener("submit", this.validatePresenceHandler);
    }

    // NOTE: Selected values are initialized through the selectedValue property
    // and marked as items load via _updateSelectionState()
    this.setupInfiniteScroll();

    // Apply selected display if provided, otherwise preload if needed
    if (this.hasSelectedDisplayValue && this.selectedDisplayValue.length > 0) {
      this._applySelectedDisplay();
      this._showPromptOption();

      if (this.multiselectValue) {
        this.selectedDisplayValue.forEach((item) => {
          this.selectedItems.set(String(item.value), item);
        });
      } else {
        this.selectedValue = [String(this.selectedDisplayValue[0].value)];
      }
    }
  }

  disconnect() {
    document.removeEventListener("click", this.clickOutsideHandler);

    if (this.nextObserver) {
      this.nextObserver.disconnect();
    }

    if (this.hasPresenceValidationErrorTarget) {
      document.removeEventListener("submit", this.validatePresenceHandler);
    }
  }

  _afterToggleMenu() {
    if (!this.loadingErrorTarget.classList.contains("hidden")) {
      this._hideErrorMessage();
    }

    if (this.items.length === 0 && !this.loadedPages.has(1)) {
      this.loadInitialData();
    }

    // Focus search input
    if (this.hasMenuFilterTarget) {
      this.menuFilterTarget.focus();
    }

    if (this.promptValue && this._hasSelectedItems()) {
      this._showPromptOption();
    }
  }

  loadInitialData() {
    this.currentPage = 1;
    this.items = [];
    this.loadedPages.clear();
    this.loadData();
  }

  loadData() {
    if (this.ajaxRequest) {
      this.ajaxRequest.abort();
    }

    // "Cache"
    if (this.loadedPages.has(this.currentPage)) {
      return;
    }

    const url = new URL(this.endpointValue, window.location.origin);
    url.searchParams.set("page", this.currentPage);
    url.searchParams.set("per_page", this.perPageValue || 20);

    if (this.searchTerm) {
      url.searchParams.set("search", this.searchTerm);
    }

    Rails.ajax({
      url: url,
      type: "GET",
      beforeSend: (xhr) => {
        this.ajaxRequest = xhr;
        return true;
      },
      contentType: "application/json",
      success: this._processLoadedData.bind(this),
      error: (e) => {
        console.error(e);
        this._showError();
      },
    });
  }

  _processLoadedData(data) {
    const { items, meta } = data;

    this.loadedPages.add(this.currentPage);

    // Internal state and all that jazz
    if (items && items.length > 0) {
      this.items =
        this.searchTerm || this.currentPage === 1
          ? items
          : [...this.items, ...items];

      this.totalPages = meta?.total_pages || 1;

      // Render only the new items slice for pagination efficiency
      // This prevents re-rendering all items when loading more pages
      if (this.currentPage === 1) {
        this._renderItems();
      } else {
        this._renderItems(items);
      }

      if (this.currentPage === 1 && !this.nextObserver) {
        this.setupInfiniteScroll();
      }

      this.handleScrollTrigger();
      this.toggleInfiniteScroll();
      this._updateNoResultsMessage();
    } else if (this.currentPage === 1) {
      this._showNoResults();
    }
  }

  _renderItems(slice) {
    if (this.currentPage === 1) {
      this.itemsContainerTarget.innerHTML = "";

      this.items.forEach((item) => {
        const itemElement = this._createItemElement(item);
        this.itemsContainerTarget.appendChild(itemElement);
      });
    } else {
      slice.forEach((item) => {
        const itemElement = this._createItemElement(item);
        this.itemsContainerTarget.appendChild(itemElement);
      });
    }

    // Update selection state
    this._updateSelectionState();
  }

  _createItemElement(item) {
    const newItemElement = document.importNode(
      this.itemTemplateTarget.content,
      true,
    ).firstElementChild;

    const isSelected = this.multiselectValue
      ? this.selectedItems.has(String(item.value))
      : this.selectedValue && this.selectedValue.includes(String(item.value));

    newItemElement.dataset.value = item.value;
    newItemElement.dataset.action = item.disabled
      ? ""
      : "click->components--admin-dropdowns-async-dropdown#selectItem";

    const textDiv = newItemElement.querySelector(".js-menu-option-text");
    textDiv.textContent = item.text;

    const subtitleDiv = newItemElement.querySelector(".js-menu-option-subtitle");
    if (subtitleDiv && item.subtitle) {
      subtitleDiv.textContent = item.subtitle;
      subtitleDiv.classList.remove("hidden");
    }

    if (isSelected) {
      newItemElement.classList.add("js-selected");
    } else {
      const iconDiv = newItemElement.querySelector("span");
      iconDiv.classList.add("hidden");
    }

    return newItemElement;
  }

  selectItem(event) {
    const itemElement = event.currentTarget;
    const isDisabled = itemElement.dataset.action == "";

    if (isDisabled) return;

    if (this.multiselectValue) {
      this._toggleMultiselectItem(itemElement);
    } else {
      this._selectSingleItem(itemElement);
      this.toggleMenu();
    }

    this._triggerChangeEvent();
    this._hideErrorMessage();
    this._toggleActiveClass();
  }

  // NOTE: Selected element styling is handled through CSS classes
  // The 'selected' class applies the appropriate background and color
  _selectSingleItem(itemElement) {
    this.itemsContainerTarget.querySelectorAll("span").forEach((el) => {
      el.classList.add("hidden");
    });

    this.itemsContainerTarget
      .querySelector(".js-selected")
      ?.classList.remove("js-selected");

    itemElement.querySelector("span").classList.remove("hidden");

    const { value, text } = this._extractMetaDataFromElement(itemElement);

    itemElement.classList.add("js-selected");
    this.hiddenFieldTarget.value = value;
    this._updateLabel(text);

    // Internal state
    this.selectedValue = [String(itemElement.dataset.value)];
  }

  _extractMetaDataFromElement(itemElement) {
    const { value } = itemElement.dataset;
    const textEl = itemElement.querySelector(".js-menu-option-text");

    return {
      value,
      text: (textEl ?? itemElement.firstElementChild).textContent,
    };
  }

  _toggleMultiselectItem(itemElement) {
    const { text, value } = this._extractMetaDataFromElement(itemElement);

    if (this.selectedItems.has(String(value))) {
      this.selectedItems.delete(String(value));

      itemElement.classList.remove("js-selected");
      itemElement.querySelector("span").classList.add("hidden");
    } else {
      this.selectedItems.set(String(value), { value, text });

      itemElement.classList.add("js-selected");
      itemElement.querySelector("span").classList.remove("hidden");
    }

    this._updateMultiselectHiddenFields();
    this._updateMultiselectLabel();
  }

  _updateMultiselectHiddenFields() {
    this.multiselectHiddenFieldsContainerTarget.innerHTML = "";

    this.selectedItems.forEach((item, value) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = this.hiddenFieldTargets[0]?.name || "";
      input.value = value;
      input.setAttribute(
        "data-components--admin-dropdowns-async-dropdown-target",
        "hiddenField",
      );

      this.multiselectHiddenFieldsContainerTarget.appendChild(input);
    });

    if (this.selectedItems.size === 0) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = this.hiddenFieldTargets[0]?.name || "";
      input.value = "";
      input.setAttribute(
        "data-components--admin-dropdowns-async-dropdown-target",
        "hiddenField",
      );
      this.multiselectHiddenFieldsContainerTarget.appendChild(input);
    }
  }

  _updateMultiselectLabel() {
    const count = this.selectedItems.size;
    const singleItemText =
      count === 1
        ? this.selectedItems.get(String(this.selectedItems.keys().next().value))
            .text
        : "";

    // Use base class method
    super._updateMultiselectLabel(count, singleItemText);
  }

  _updateLabel(text) {
    this._updateLabelWithSelection(text);
  }

  setupInfiniteScroll() {
    if (this.hasMore && this.hasInfiniteScrollTriggerTarget) {
      if (this.nextObserver) {
        this.nextObserver.disconnect();
      }

      this.nextObserver = this.initializeObserver(this.intersectionCallback);
      this.nextObserver.observe(this.infiniteScrollTriggerTarget);
      this.toggleInfiniteScroll();
    }
  }

  initializeObserver(callback) {
    const root = this.menuTarget;

    return new IntersectionObserver(callback.bind(this), {
      root: root,
      threshold: 1.0,
    });
  }

  async intersectionCallback(entries) {
    entries.forEach((entry) => {
      if (entry.intersectionRatio !== 1) {
        return;
      }

      if (!this.hasMore) {
        this.infiniteScrollTriggerTarget.classList.add("hidden");

        return;
      }

      this.loadMore();
    });
  }

  toggleInfiniteScroll() {
    if (!this.hasInfiniteScrollTriggerTarget) return;

    this.infiniteScrollTriggerTarget.classList.toggle("hidden", !this.hasMore);
  }

  get hasMore() {
    return this.currentPage < this.totalPages;
  }

  handleScrollTrigger() {
    if (!this.hasInfiniteScrollTriggerTarget) return;

    this.manageObserver(
      this.nextObserver,
      this.infiniteScrollTriggerTarget,
      this.hasMore,
    );
  }

  manageObserver(observer, target, hasPage) {
    if (hasPage) {
      observer.disconnect();
      observer.observe(target);
    } else {
      target.classList.add("hidden");
    }
  }

  searchWithDebounce(event) {
    this.searchTerm = event.target.value.trim();
    this._toggleClearFilterIcon();
    this.searchHandler();
  }

  performSearch() {
    this.currentPage = 1;
    this.loadedPages.clear();
    this.loadData();
  }

  clearSearch() {
    this.menuFilterTarget.value = "";
    this.searchTerm = "";
    this._toggleClearFilterIcon();
    this.performSearch();

    this.infiniteScrollTriggerTarget.classList.add("hidden");
  }

  loadMore() {
    if (this.hasMore) {
      this.currentPage++;
      this.loadData();
    }
  }

  // Override base class validation method
  _hasValidValue() {
    if (this.multiselectValue) {
      return this.selectedItems.size > 0;
    }
    return this.hiddenFieldTarget?.value?.trim();
  }

  // Helper methods
  _showNoResults() {
    this.noResultsMessageTarget.classList.remove("hidden");
    this.itemsContainerTarget.classList.add("hidden");
    this.infiniteScrollTriggerTarget.classList.add("hidden");
  }

  _updateNoResultsMessage() {
    if (this.items.length === 0) {
      this.noResultsMessageTarget.classList.remove("hidden");
      this.itemsContainerTarget.classList.add("hidden");
    } else {
      this.noResultsMessageTarget.classList.add("hidden");
      this.itemsContainerTarget.classList.remove("hidden");
    }
  }

  _toggleClearFilterIcon() {
    this._toggleFilterIcon(
      this.clearFilterIconTarget,
      this.menuFilterTarget.value,
    );
  }

  _updateSelectionState() {
    this.itemsContainerTarget.querySelectorAll("[data-value]").forEach((el) => {
      const value = el.dataset.value; // Always parsed as a string
      const isSelected = this.multiselectValue
        ? this.selectedItems.has(String(value))
        : this.selectedValue && this.selectedValue.includes(String(value));

      if (isSelected) {
        el.querySelector("span").classList.remove("hidden");
      } else {
        el.querySelector("span").classList.add("hidden");
      }
    });
  }

  _applySelectedDisplay() {
    if (this.multiselectValue) {
      this.selectedDisplayValue.forEach((item) => {
        if (item && item.value && item.text) {
          this.selectedItems.set(String(item.value), {
            value: String(item.value),
            text: item.text,
          });
        }
      });
      this._updateMultiselectLabel();
    } else {
      const item = this.selectedDisplayValue[0];
      if (item && item.text) {
        this._updateLabel(item.text);
      }
    }

    this._toggleActiveClass();
    this._updateSelectionState();
  }

  _triggerChangeEvent() {
    const value = this.multiselectValue
      ? Array.from(this.selectedItems.keys())
      : this.hiddenFieldTarget?.value;

    // Trigger the standard change event on hidden field for backward compatibility
    super._triggerChangeEvent();

    // Dispatch a custom event from the component element for other controllers to listen to
    const event = new CustomEvent("promote:async-dropdown:change", {
      bubbles: true,
      detail: {
        value,
        text: this.multiselectValue
          ? Array.from(this.selectedItems.values()).map((item) => item.text)
          : this.labelTarget.textContent,
      },
    });

    this.element.dispatchEvent(event);
  }

  _showError() {
    this.itemsContainerTarget.innerHTML = "";
    this.loadingErrorTarget.classList.remove("hidden");
  }

  _hasSelectedItems() {
    if (this.multiselectValue) {
      return this.selectedItems.size > 0;
    }
    return this.selectedValue && this.selectedValue.length > 0;
  }

  resetDropdown(event) {
    this._updateLabelWithSelection(this.promptOptionTarget.innerHTML);

    // Clean up internal state and close menu
    this.selectedValue = [];
    if (this.multiselectValue) {
      this.selectedItems.clear();
    }

    this._resetState();
    this.toggleMenu();
  }

  _resetHiddenFieldValue() {
    this.hiddenFieldTarget.value = "";
  }

  _resetState() {
    this._hidePromptOption();
    this._resetHiddenFieldValue();
    this._deselectItems();
    this._triggerChangeEvent();
    this._removeActiveClass();
  }

  _deselectItems() {
    this.itemsContainerTarget.querySelectorAll(".js-selected").forEach((el) => {
      el.classList.remove("js-selected");
      el.querySelector("span").classList.add("hidden");
    });
  }

  _removeActiveClass() {
    this.componentTarget.firstElementChild.classList.remove("active");
  }
}
